import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../../firebaseConfig";
import { collection, addDoc, getDoc, doc, Timestamp } from "firebase/firestore";
import "./MobileLogging.css";

// ============================================================================
// EXACT LOGIC HELPERS FROM DESKTOP (DO NOT MODIFY)
// ============================================================================

const toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const toHHMM = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const getStepForType = (type) => (type === "admin" ? 5 : 10);
const ceilToStep = (mins, step) => Math.ceil(mins / step) * step;

const getDayBounds = (dateStr) => {
  if (!dateStr) return { startMin: 0, endMin: 0, label: "" };

  const [y, m, d] = dateStr.split("-").map(Number);
  const dayOfWeek = new Date(y, m - 1, d).getDay(); // 0=Sun, 6=Sat
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  // Weekday: 2:00 PM - 10:30 PM
  // Weekend: 8:30 AM - 6:00 PM
  const startMin = isWeekend ? 8 * 60 + 30 : 14 * 60;
  const endMin = isWeekend ? 18 * 60 : 22 * 60 + 30;

  const label = isWeekend ? "8:30 AM – 6:00 PM" : "2:00 PM – 10:30 PM";
  return { startMin, endMin, label };
};

const getOptionsForDate = (dateStr, step) => {
  if (!dateStr) return [];
  const { startMin, endMin } = getDayBounds(dateStr);

  const options = [];
  for (let mins = startMin; mins <= endMin; mins += step) {
    const hours = Math.floor(mins / 60);
    const m = mins % 60;

    const value = `${String(hours).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    const label = `${h12}:${String(m).padStart(2, "0")} ${ampm}`;

    options.push({ value, label });
  }
  return options;
};

const sanitizeEntriesForDate = (rawEntries, dateStr) => {
  const { startMin, endMin } = getDayBounds(dateStr);
  const next = rawEntries.map((e) => ({ ...e }));

  for (let i = 0; i < next.length; i++) {
    const step = getStepForType(next[i].type);

    // compute minimum allowed start (>= day start and >= previous end)
    const prevEndMin = i > 0 ? toMinutes(next[i - 1].endTime) : null;
    let minStartAllowed = Math.max(startMin, prevEndMin ?? startMin);
    minStartAllowed = ceilToStep(minStartAllowed, step);

    // --- START ---
    const sRaw = toMinutes(next[i].startTime);
    if (sRaw != null) {
      let s = Math.max(sRaw, minStartAllowed);
      s = ceilToStep(s, step);

      if (s > endMin) next[i].startTime = "";
      else next[i].startTime = toHHMM(s);
    }

    // --- END ---
    const startNowMin = toMinutes(next[i].startTime);
    const eRaw = toMinutes(next[i].endTime);

    if (eRaw != null) {
      const baseMinEnd = (startNowMin != null ? startNowMin : minStartAllowed) + step;
      const minEndAllowed = ceilToStep(baseMinEnd, step);

      let e = ceilToStep(eRaw, step);

      if (e < minEndAllowed) next[i].endTime = "";
      else if (e > endMin) next[i].endTime = "";
      else next[i].endTime = toHHMM(e);
    }

    // enforce end > start
    const s2 = toMinutes(next[i].startTime);
    const e2 = toMinutes(next[i].endTime);
    if (s2 != null && e2 != null && e2 <= s2) next[i].endTime = "";

    // cascade: if current end exists, ensure next start >= current end (snapped to next row step)
    const endNowMin = toMinutes(next[i].endTime);
    if (endNowMin != null && next[i + 1]) {
      const nextStep = getStepForType(next[i + 1].type);
      let minNextStart = Math.max(startMin, endNowMin);
      minNextStart = ceilToStep(minNextStart, nextStep);

      const nextStartMin = toMinutes(next[i + 1].startTime);
      if (nextStartMin == null || nextStartMin < minNextStart) {
        next[i + 1].startTime = toHHMM(minNextStart);

        const nextEndMin = toMinutes(next[i + 1].endTime);
        if (nextEndMin != null && nextEndMin <= minNextStart) next[i + 1].endTime = "";
      }
    }
  }

  return next;
};

// Helper for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// ============================================================================
// MOBILE COMPONENT (Visuals changed, Logic maintained)
// ============================================================================

export default function MobileTutorLogging() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [userData, setUserData] = useState({
    name: "",
    adminRate: 0,
    teachingRate: 0,
  });

  const [date, setDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0]; // "YYYY-MM-DD"
  });

  const [entries, setEntries] = useState([
    { type: "admin", startTime: "", endTime: "", startMode: "select", endMode: "select" },
  ]);

  // Fetch User Data (Same as Desktop)
  useEffect(() => {
    const fetchUserData = async () => {
      if (auth.currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({
              name: data.name,
              adminRate: Number(data.adminRate) || 0,
              teachingRate: Number(data.teachingRate) || 0,
            });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
      setLoading(false);
    };

    fetchUserData();
  }, []);

  const dayBounds = useMemo(() => getDayBounds(date), [date]);

  // Options per type
  const optionsAdmin = useMemo(() => getOptionsForDate(date, 5), [date]);
  const optionsTeaching = useMemo(() => getOptionsForDate(date, 10), [date]);
  const getOptionsForType = (type) => (type === "admin" ? optionsAdmin : optionsTeaching);

  // Re-sanitize when date changes
  useEffect(() => {
    setEntries((prev) => sanitizeEntriesForDate(prev, date));
  }, [date]);

  // Helper to filter dropdown options based on logic
  const getStartOptionsForRow = (rowIndex, draftEntries) => {
    const row = draftEntries[rowIndex];
    const step = getStepForType(row.type);
    const options = getOptionsForType(row.type);

    const prevEndMin = rowIndex > 0 ? toMinutes(draftEntries[rowIndex - 1].endTime) : null;
    let minStart = Math.max(dayBounds.startMin, prevEndMin ?? dayBounds.startMin);
    minStart = ceilToStep(minStart, step);

    return options.filter((t) => toMinutes(t.value) >= minStart);
  };

  const getEndOptionsForRow = (rowIndex, draftEntries) => {
    const row = draftEntries[rowIndex];
    const step = getStepForType(row.type);
    const options = getOptionsForType(row.type);

    const prevEndMin = rowIndex > 0 ? toMinutes(draftEntries[rowIndex - 1].endTime) : null;
    let minStartAllowed = Math.max(dayBounds.startMin, prevEndMin ?? dayBounds.startMin);
    minStartAllowed = ceilToStep(minStartAllowed, step);

    const startMin = toMinutes(row.startTime);
    const baseMinEnd = (startMin != null ? startMin : minStartAllowed) + step;
    const minEnd = ceilToStep(baseMinEnd, step);

    return options.filter((t) => toMinutes(t.value) >= minEnd);
  };

  // Handlers (Exact copy of Desktop logic logic)
  const handleEntryChange = (index, field, value) => {
    setEntries((prev) => {
      const next = prev.map((e) => ({ ...e }));
      
      // Mobile logic: We are only using Select, so no custom mode toggle logic needed inside here
      // But we keep the sanitization logic intact.
      next[index][field] = value;

      // if start changes, clear end if invalid
      if (field === "startTime") {
        const s = toMinutes(next[index].startTime);
        const e = toMinutes(next[index].endTime);
        if (s != null && e != null && e <= s) next[index].endTime = "";
      }

      // if end changes, push next row start forward
      if (field === "endTime") {
        const endNowMin = toMinutes(next[index].endTime);
        if (endNowMin != null && next[index + 1]) {
          const nextStep = getStepForType(next[index + 1].type);
          let minNextStart = Math.max(dayBounds.startMin, endNowMin);
          minNextStart = ceilToStep(minNextStart, nextStep);

          const nextStartMin = toMinutes(next[index + 1].startTime);
          if (nextStartMin == null || nextStartMin < minNextStart) {
            next[index + 1].startTime = toHHMM(minNextStart);

            const nextEndMin = toMinutes(next[index + 1].endTime);
            if (nextEndMin != null && nextEndMin <= minNextStart) next[index + 1].endTime = "";
          }
        }
      }

      return sanitizeEntriesForDate(next, date);
    });
  };

  const addEntry = () => {
    setEntries((prev) => {
      const last = prev[prev.length - 1];
      const lastEnd = last?.endTime || "";
      const next = [
        ...prev,
        { type: "admin", startTime: lastEnd, endTime: "", startMode: "select", endMode: "select" },
      ];
      return sanitizeEntriesForDate(next, date);
    });
  };

  const removeEntry = (index) => {
    setEntries((prev) => sanitizeEntriesForDate(prev.filter((_, i) => i !== index), date));
  };

  const handleSubmit = async () => {
    if (entries.some((e) => !e.startTime || !e.endTime)) {
      alert("Please enter both start and end times for all rows.");
      return;
    }

    if (!window.confirm("Are you sure you want to submit these hours?")) return;

    setSubmitting(true);

    try {
      const batchPromises = entries.map(async (entry) => {
        const startTs = Timestamp.fromDate(new Date(`${date} ${entry.startTime}`));
        const endTs = Timestamp.fromDate(new Date(`${date} ${entry.endTime}`));

        const durationMs = endTs.toDate() - startTs.toDate();
        const rawHours = durationMs / (1000 * 60 * 60);

        if (rawHours <= 0) throw new Error("End time must be after start time");

        const roundedHours = Math.round(rawHours * 100) / 100;
        const rateUsed = entry.type === "admin" ? userData.adminRate : userData.teachingRate;
        const totalPay = parseFloat((roundedHours * rateUsed).toFixed(2));

        const [y, m, d] = date.split("-");
        const formattedDate = `${m}/${d}/${y}`; 

        const record = {
          uid: auth.currentUser.uid,
          tutorName: userData.name,
          date: formattedDate,
          type: entry.type,
          startTime: startTs,
          endTime: endTs,
          hours: roundedHours,
          rateUsed: rateUsed,
          totalPay: totalPay,
          status: "approved",
          source: "tutor_logging",
        };

        return addDoc(collection(db, "payroll_records"), record);
      });

      await Promise.all(batchPromises);

      alert("✅ Hours submitted successfully!");
      setEntries([
        { type: "admin", startTime: "", endTime: "", startMode: "select", endMode: "select" },
      ]);
    } catch (error) {
      console.error("Error submitting:", error);
      alert("Error submitting hours. Please check times.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{padding:20}}>Loading...</div>;
  }

  // --- RENDER (Mobile UI Structure) ---
  return (
    <div className="ml-container">
      <div className="ml-content">

        {/* Context note */}
        <p className="ml-context">
          Select a date and add time blocks below. All requests are reviewed before payroll processing.
        </p>

        {/* Meta Info */}
        <div className="ml-meta-row">
          <div className="ml-meta-group">
            <label className="ml-meta-label">Tutor</label>
            <div className="ml-tutor-name">{userData.name || "—"}</div>
          </div>
          <div className="ml-meta-group" style={{ alignItems: 'flex-end' }}>
            <label className="ml-meta-label">Date</label>
            <div className="ml-date-btn">
              {formatDateDisplay(date)}
              {/* Hidden Date Input */}
              <input 
                type="date" 
                className="ml-date-input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Form Entries */}
        {entries.map((entry, index) => {
          const startOptions = getStartOptionsForRow(index, entries);
          const endOptions = getEndOptionsForRow(index, entries);
          const startDisabled = startOptions.length === 0;
          const endDisabled = !entry.startTime;

          return (
            <div key={index} className="ml-form-card">
              {/* Remove Button (only if > 1) */}
              {entries.length > 1 && (
                <button 
                  className="ml-remove-btn" 
                  onClick={() => removeEntry(index)}
                >
                  ✕
                </button>
              )}

              {/* Field 1: Type */}
              <div className="ml-input-group">
                <label className="ml-input-label">Type</label>
                <div className="ml-select-wrapper">
                  <select 
                    className="ml-select"
                    value={entry.type}
                    onChange={(e) => handleEntryChange(index, "type", e.target.value)}
                  >
                    <option value="admin">Admin</option>
                    <option value="teaching">Teaching</option>
                  </select>
                  <div className="ml-arrow-icon">▼</div>
                </div>
              </div>

              {/* Field 2: Start Time */}
              <div className="ml-input-group">
                <label className="ml-input-label">Start Time</label>
                <div className="ml-select-wrapper">
                  <select 
                    className="ml-select"
                    value={entry.startTime}
                    onChange={(e) => handleEntryChange(index, "startTime", e.target.value)}
                    disabled={startDisabled}
                  >
                    <option value="">{startDisabled ? "No valid start times" : "Select..."}</option>
                    {startOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <div className="ml-arrow-icon">▼</div>
                </div>
              </div>

              {/* Field 3: End Time */}
              <div className="ml-input-group" style={{ marginBottom: 0 }}>
                <label className="ml-input-label">End Time</label>
                <div className="ml-select-wrapper">
                  <select 
                    className="ml-select"
                    value={entry.endTime}
                    onChange={(e) => handleEntryChange(index, "endTime", e.target.value)}
                    disabled={endDisabled}
                  >
                    <option value="">{endDisabled ? "Select a start time first" : "Select..."}</option>
                    {!endDisabled && endOptions.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <div className="ml-arrow-icon">▼</div>
                </div>
                <div className="ml-micro-help">End must be after start.</div>
              </div>
            </div>
          );
        })}

        {/* Add Block Action */}
        <button className="ml-add-block-btn" onClick={addEntry}>
          + Add Another Block
        </button>

      </div>

      {/* Fixed Submit Button */}
      <div className="ml-submit-container">
        <button
          className="ml-submit-btn"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : "Submit Request"}
        </button>
      </div>
    </div>
  );
}